import type { ReactNode } from 'react'
import { formatCost, formatTokenCell } from '@/lib/analyticsFormatters'

export interface TokenUsageTableRow {
  key: string
  label: ReactNode
  inputTokens: number
  outputTokens: number
  cachedTokens: number
  totalTokens: number
  costUsd: number
}

interface TokenUsageTableProps {
  ariaLabel: string
  firstColumnLabel: string
  rows: TokenUsageTableRow[] | undefined
  isLoading: boolean
  isError: boolean
  rowTestId: string
  emptyTestId: string
}

const HEADER_CELL = 'px-4 py-3 text-left text-sm font-medium text-foreground'
const TOTALS_CELL = 'px-4 py-3 text-sm font-semibold'

export function TokenUsageTable({
  ariaLabel,
  firstColumnLabel,
  rows,
  isLoading,
  isError,
  rowTestId,
  emptyTestId,
}: TokenUsageTableProps) {
  if (isError) {
    return <div role="alert" className="text-sm text-destructive py-4">Failed to load analytics data.</div>
  }

  if (isLoading || !rows) {
    return (
      <div role="status" aria-live="polite" className="text-sm text-muted-foreground py-4">
        Loading...
      </div>
    )
  }

  const totals = rows.reduce(
    (sum, row) => ({
      input: sum.input + row.inputTokens,
      output: sum.output + row.outputTokens,
      cached: sum.cached + row.cachedTokens,
      total: sum.total + row.totalTokens,
      cost: sum.cost + row.costUsd,
    }),
    { input: 0, output: 0, cached: 0, total: 0, cost: 0 },
  )

  return (
    <div className="rounded-2xl border overflow-hidden">
      <table aria-label={ariaLabel} className="w-full table-fixed break-words bg-table-row text-card-foreground">
        <thead>
          <tr className="bg-table-header border-b">
            <th className={`${HEADER_CELL} w-1/3`}>{firstColumnLabel}</th>
            <th className={HEADER_CELL}>Tokens Used</th>
            <th className={HEADER_CELL}>Input Token Usage</th>
            <th className={HEADER_CELL}>Output Token Usage</th>
            <th className={HEADER_CELL}>Cached Token Usage</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Amount ($)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid={emptyTestId}>
                No usage recorded for this period.
              </td>
            </tr>
          )}
          {rows.map(row => (
            <tr
              key={row.key}
              className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
              data-testid={rowTestId}
            >
              <td className="px-4 py-3">{row.label}</td>
              <td className="px-4 py-3 text-sm">{formatTokenCell(row.totalTokens, row.totalTokens, row.costUsd)}</td>
              <td className="px-4 py-3 text-sm">{formatTokenCell(row.totalTokens, row.inputTokens, row.costUsd)}</td>
              <td className="px-4 py-3 text-sm">{formatTokenCell(row.totalTokens, row.outputTokens, row.costUsd)}</td>
              <td className="px-4 py-3 text-sm">{formatTokenCell(row.totalTokens, row.cachedTokens, row.costUsd)}</td>
              <td className="px-4 py-3 text-sm text-right">{formatCost(row.costUsd)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-table-row shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
            <td className={TOTALS_CELL}>Total</td>
            <td className={TOTALS_CELL}>{formatTokenCell(totals.total, totals.total, totals.cost)}</td>
            <td className={TOTALS_CELL}>{formatTokenCell(totals.total, totals.input, totals.cost)}</td>
            <td className={TOTALS_CELL}>{formatTokenCell(totals.total, totals.output, totals.cost)}</td>
            <td className={TOTALS_CELL}>{formatTokenCell(totals.total, totals.cached, totals.cost)}</td>
            <td className={`${TOTALS_CELL} text-right`}>{formatCost(totals.cost)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
